import {
  ModalDialog,
  ModalDialogActions,
  ModalDialogDescription,
  ModalDialogDescriptionParagraph,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogTitle,
} from '@/components/ModalDialog'
import { Button } from '@/components/Button'
import { AutoSkeleton, Skeleton } from '@/components/Skeleton'

interface ArchiveJobDialogProps {
  /**
   * The name of the job to delete.
   */
  jobName: string
  /**
   * Whether the dialog is open.
   */
  isOpen: boolean
  /**
   * Whether the dialog is loading.
   */
  isLoading?: boolean
  /**
   * Whether the delete operation is pending.
   */
  isPending: boolean
  /**
   * Called when the user confirms the deletion.
   */
  onArchive: () => void
  /**
   * Called when the dialog is closed.
   */
  onClose: () => void
  /**
   * Test ID attribute for the dialog.
   */
  testId?: string
  /**
   * Test ID attribute for the confirm delete button.
   */
  confirmDeleteTestId?: string
  /**
   * Test ID attribute for the cancel button.
   */
  cancelTestId?: string
}

/**
 * Presentation component for the archive job confirmation dialog.
 * Displays a modal asking the user to confirm job deletion with
 * appropriate loading and pending states.
 */
export function ArchiveJobDialog({
  jobName,
  isOpen,
  isLoading,
  isPending,
  onArchive,
  onClose,
  testId,
  confirmDeleteTestId,
  cancelTestId,
}: ArchiveJobDialogProps) {
  return (
    <ModalDialog
      testId={testId}
      size="base"
      isOpen={isOpen}
      onClose={onClose}
      isDismissable={!isPending && !isLoading}
    >
      <ModalDialogHeader>
        <ModalDialogTitle>
          <AutoSkeleton isSkeleton={isLoading}>Delete job?</AutoSkeleton>
        </ModalDialogTitle>
        <ModalDialogDescription>
          <ModalDialogDescriptionParagraph>
            {isLoading ? (
              <>
                <Skeleton width="92%" />
                <Skeleton width="90%" />
                <Skeleton width="95%" />
                <Skeleton width="75%" />
              </>
            ) : (
              <>
                <span className="text-headline-md">{jobName}</span> is currently
                under construction. Are you sure you want to delete it? This
                action is permanent and cannot be undone.
              </>
            )}
          </ModalDialogDescriptionParagraph>
        </ModalDialogDescription>
      </ModalDialogHeader>
      <ModalDialogFooter>
        <ModalDialogActions>
          <Button
            label="Yes, delete"
            isSkeleton={isLoading}
            onPress={onArchive}
            isPending={isPending}
            isDisabled={isLoading}
            testId={confirmDeleteTestId}
          />
          <Button
            isDisabled={isPending || isLoading}
            isSkeleton={isLoading}
            label="Cancel"
            onPress={onClose}
            variant="outline"
            testId={cancelTestId}
          />
        </ModalDialogActions>
      </ModalDialogFooter>
    </ModalDialog>
  )
}
